// src/components/UserMenu.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Settings, LogOut, ChevronDown, ShoppingBag, Heart } from 'lucide-react';
import { authAPI } from '../services/api';

const UserMenu = ({ currentUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 處理登出
  const handleLogout = () => {
    if (window.confirm(t('user.confirmLogout') || '確定要登出嗎？')) {
      authAPI.logout();
      // logout 方法會自動跳轉到 /auth
    }
  };

  // 如果未登入，顯示登入按鈕
  if (!currentUser) {
    return (
      <button
        onClick={() => navigate('/auth')}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors"
      >
        <User className="w-5 h-5" />
        <span className="text-sm hidden md:inline">{t('common.login')}</span>
      </button>
    );
  }

  // 已登入，顯示下拉選單
  return (
    <div className="relative" ref={dropdownRef}>
      {/* 用戶按鈕 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-white text-orange-500 flex items-center justify-center font-bold">
          {currentUser.name?.charAt(0).toUpperCase() || 'U'}
        </div>
        <span className="text-sm hidden md:inline max-w-[100px] truncate">
          {currentUser.name}
        </span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* 下拉選單 */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
          {/* 用戶資訊頭部 */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                {currentUser.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {currentUser.name}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {currentUser.email}
                </p>
              </div>
            </div>
          </div>

          {/* 選單項目 */}
          <div className="py-2">
            {/* 我的訂單 */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/orders');
              }}
              className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors"
            >
              <ShoppingBag size={18} className="text-gray-600" />
              <span className="text-gray-700">{t('user.myOrders')}</span>
            </button>

            {/* 我的收藏 */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/favorites');
              }}
              className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors"
            >
              <Heart size={18} className="text-gray-600" />
              <span className="text-gray-700">{t('user.myFavorites')}</span>
            </button>

            {/* 分隔線 */}
            <div className="my-2 border-t border-gray-100"></div>

            {/* 帳戶設定 */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile');
              }}
              className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors"
            >
              <Settings size={18} className="text-gray-600" />
              <span className="text-gray-700">{t('user.accountSettings')}</span>
            </button>

            {/* 分隔線 */}
            <div className="my-2 border-t border-gray-100"></div>

            {/* 登出 */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-red-50 transition-colors text-red-600"
            >
              <LogOut size={18} />
              <span className="font-medium">{t('user.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;